'use client';

import { useEffect, useRef, useState } from 'react';
import { NavPill } from './nav-pill';
import type { IconName } from './icon';
import styles from './nav-rail.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

export interface NavRailItem {
  id: string;
  label: string;
  href?: string;
  icon?: IconName;
  badge?: number | string | null;
}

interface NavRailProps {
  items: NavRailItem[];
  /** id of the active item; the sliding pill glides to it. */
  active: string;
  orientation?: 'horizontal';
  className?: string;
}

/**
 * A set of NavPills with ONE measured sliding active-pill that glides between
 * items as `active` changes (spring-eased). The canonical sliding treatment
 * for a horizontal rail. Ported from the #302 element library (`NavRail`).
 * When no item matches `active` (route not in the rail), the pill fades out.
 */
export function NavRail({ items, active, orientation = 'horizontal', className }: NavRailProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState({ x: 0, y: 0, w: 0, h: 0, ready: false });
  const key = items.map((i) => `${i.id}|${i.label}`).join('');

  useEffect(() => {
    const measure = () => {
      const c = railRef.current;
      if (!c) return;
      const el = c.querySelector<HTMLElement>(`[data-nav="${active}"]`);
      if (!el) {
        setPill((p) => (p.ready ? { ...p, ready: false } : p));
        return;
      }
      setPill({ x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight, ready: true });
    };
    measure();
    const raf = requestAnimationFrame(measure);
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(measure);
    }
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
    };
  }, [active, orientation, key]);

  return (
    <div ref={railRef} className={cx(styles.navRail, styles[orientation], className)}>
      <span
        className={styles.pill}
        aria-hidden="true"
        style={{
          transform: `translate(${pill.x}px, ${pill.y}px)`,
          width: pill.w,
          height: pill.h,
          opacity: pill.ready ? 1 : 0,
        }}
      />
      {items.map((it) => (
        <NavPill
          key={it.id}
          data-nav={it.id}
          href={it.href}
          active={it.id === active}
          orientation={orientation}
          icon={it.icon}
          badge={it.badge}
          sliding
        >
          {it.label}
        </NavPill>
      ))}
    </div>
  );
}
